/**
 * useWizardState - Custom hook for wizard state management
 * 
 * Handles:
 * - Initial state from URL params, navigation state, or localStorage
 * - Step navigation (next, back, goToStep)
 * - Data updates and merging
 * - LocalStorage backup/restore
 * - Highest completed step tracking
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import api from '../../../../utils/api';

interface WizardData {
  client_name: string;
  claimant_name: string;
  business_address: string;
  zone: string;
  business_type: string;
  financial_data: Record<string, any>;
  calculation_mode?: string;
  [key: string]: any;
}

interface NavigationState {
  freshDraftData?: any;
  freshDraftStep?: number;
  draftId?: string;
  fromDashboard?: boolean;
}

const EMPTY_DATA: WizardData = {
  client_name: '',
  claimant_name: '',
  business_address: '',
  zone: '',
  business_type: '',
  financial_data: {}
};

export function useWizardState() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // URL params
  const resumeReportId = searchParams.get('resume');
  const urlDraftId = searchParams.get('draft');
  const urlStep = searchParams.get('step');
  const urlSection = searchParams.get('section');
  
  // Navigation state from dashboard
  const navigationState = location.state as NavigationState | null;
  
  // Draft loading state
  const [loadingUrlDraft, setLoadingUrlDraft] = useState(!!urlDraftId);
  const [urlDraftData, setUrlDraftData] = useState<any>(null);
  
  // Load draft from URL param
  useEffect(() => {
    if (urlDraftId && !navigationState?.fromDashboard) {
      const loadDraftFromUrl = async () => {
        try {
          let draft = await api.get(`/api/v1/reports/drafts/${urlDraftId}`, { showErrorToast: false }).catch(() => null);
          
          if (!draft) {
            console.log('Draft not found, trying reports endpoint...');
            draft = await api.get(`/api/v1/reports/${urlDraftId}`, { showErrorToast: false }).catch(() => null);
          }
          
          if (draft) {
            console.log('✅ Loaded draft/report from URL param:', urlDraftId);
            localStorage.setItem('current_draft_id', urlDraftId);
            
            // Extract data and ensure draft_id is preserved
            const draftData = draft.data || draft;
            // Inject the draft ID so calculations can find it
            draftData.draft_id = urlDraftId;
            draftData.id = draftData.id || urlDraftId;
            
            setUrlDraftData(draftData);
          } else {
            console.error('Failed to load draft/report from URL');
          }
        } catch (err) {
          console.error('Error loading draft from URL:', err);
        } finally {
          setLoadingUrlDraft(false);
        }
      };
      loadDraftFromUrl();
    } else {
      setLoadingUrlDraft(false);
    }
  }, [urlDraftId, navigationState]);
  
  // Get initial state
  const getInitialState = useCallback(() => {
    // PRIORITY 0: Draft loaded from URL param
    if (urlDraftData) {
      const targetStep = urlStep ? parseInt(urlStep, 10) : 3;
      // If we have data, allow access to ALL steps (7 total)
      // This enables free navigation for loaded drafts
      const completedStep = 7; // Allow navigation to any step
      return { 
        step: targetStep, 
        highestCompletedStep: completedStep,
        data: urlDraftData,
        restored: false
      };
    }
    
    // PRIORITY 1: Fresh data from dashboard navigation
    if (navigationState?.fromDashboard && navigationState?.freshDraftData) {
      if (navigationState.draftId) {
        localStorage.setItem('current_draft_id', navigationState.draftId);
      }
      const targetStep = navigationState.freshDraftStep || 3;
      // Allow access to ALL steps for dashboard navigation
      const completedStep = 7; // Allow navigation to any step
      return { 
        step: targetStep, 
        highestCompletedStep: completedStep,
        data: navigationState.freshDraftData,
        restored: false
      };
    }
    
    // PRIORITY 2: Don't restore if resuming specific report
    if (resumeReportId) {
      return { step: 1, highestCompletedStep: 1, data: { ...EMPTY_DATA }, restored: false };
    }
    
    // PRIORITY 3: localStorage backup
    try {
      const backup = localStorage.getItem('wizard_backup');
      if (backup) {
        const parsed = JSON.parse(backup);
        const backupAge = Date.now() - new Date(parsed.timestamp).getTime();
        if (backupAge < 24 * 60 * 60 * 1000 && parsed.step && parsed.data) {
          console.log('📦 Restoring wizard backup from localStorage');
          const restoredHighest = parsed.highestCompletedStep || parsed.step;
          return { 
            step: parsed.step, 
            highestCompletedStep: restoredHighest, 
            data: parsed.data, 
            restored: !parsed.skipBanner 
          };
        }
      }
    } catch (e) {
      console.error('Failed to restore backup:', e);
    }
    
    return { step: 1, highestCompletedStep: 1, data: { ...EMPTY_DATA }, restored: false };
  }, [urlDraftData, urlStep, navigationState, resumeReportId]);
  
  const initialState = getInitialState();
  const [step, setStep] = useState(initialState.step);
  const [data, setData] = useState<WizardData>(initialState.data);
  const [showRestoredBanner, setShowRestoredBanner] = useState(initialState.restored);
  const [highestCompletedStep, setHighestCompletedStep] = useState(initialState.highestCompletedStep);
  
  // Update state when URL draft data loads
  useEffect(() => {
    if (urlDraftData) {
      setData(urlDraftData);
      const targetStep = urlStep ? parseInt(urlStep, 10) : 3;
      setStep(targetStep);
      // For loaded drafts, allow full navigation (7 steps)
      setHighestCompletedStep(7);
    }
  }, [urlDraftData, urlStep]);
  
  // Auto-backup to localStorage
  useEffect(() => {
    const hasData = data && (
      data.client_name || 
      data.claimant_name || 
      data.business_address ||
      data.zone ||
      data.business_type ||
      (data.financial_data && Object.keys(data.financial_data).length > 0)
    );
    
    if (hasData && step > 1) {
      try {
        localStorage.setItem('wizard_backup', JSON.stringify({
          step,
          highestCompletedStep,
          data,
          timestamp: new Date().toISOString()
        }));
      } catch (e) {
        console.error('Failed to save auto-backup:', e);
      }
    }
  }, [data, step, highestCompletedStep]);

  // Derive whether the user has unsaved changes worth protecting
  const hasUnsavedChanges = !!(data && step > 1 && (
    data.client_name ||
    data.claimant_name ||
    data.business_address ||
    data.zone ||
    data.business_type ||
    (data.financial_data && Object.keys(data.financial_data).length > 0)
  ));

  // Warn user on beforeunload (tab close / browser navigation) when there are unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // Navigation functions
  const updateData = useCallback((newData: Partial<WizardData>) => {
    setData(prev => ({ ...prev, ...newData }));
  }, []);
  
  const next = useCallback((newData: Partial<WizardData>) => {
    let processedData = { ...newData };
    
    // Check for yearly data format preservation
    const currentHasYearlyData = data.financial_data && 
      Object.keys(data.financial_data).some(key => key.startsWith('data_'));
    const newHasYearlyData = newData.financial_data && 
      Object.keys(newData.financial_data).some(key => key.startsWith('data_'));
    
    if (currentHasYearlyData && !newHasYearlyData && newData.financial_data && step !== 3) {
      processedData = { ...newData };
      delete processedData.financial_data;
    }
    
    const mergedData = { ...data, ...processedData };
    setData(mergedData);
    
    const targetStep = (mergedData as any).skipToStep || step + 1;
    setStep(targetStep);
    setHighestCompletedStep(prev => Math.max(prev, targetStep));
    
    // Backup
    try {
      localStorage.setItem('wizard_backup', JSON.stringify({
        step: targetStep,
        highestCompletedStep: Math.max(highestCompletedStep, targetStep),
        data: mergedData,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {
      console.error('Failed to save backup:', e);
    }
  }, [data, step, highestCompletedStep]);
  
  const back = useCallback(() => {
    setStep(prev => prev - 1);
  }, []);
  
  const goToStep = useCallback((targetStep: number) => {
    setStep(targetStep);
  }, []);
  
  const clearAndStartFresh = useCallback(() => {
    localStorage.removeItem('wizard_backup');
    localStorage.removeItem('current_draft_id');
    setStep(1);
    setData({ ...EMPTY_DATA });
    setShowRestoredBanner(false);
    setHighestCompletedStep(1);
  }, []);
  
  return {
    // State
    step,
    data,
    showRestoredBanner,
    highestCompletedStep,
    loadingUrlDraft,
    
    // URL params
    resumeReportId,
    urlDraftId,
    urlSection,
    
    // Actions
    setStep,
    setData,
    updateData,
    next,
    back,
    goToStep,
    clearAndStartFresh,
    setShowRestoredBanner,
    setHighestCompletedStep,
  };
}
